// Event.js
import './Event.css';
import { useEffect, useState, Fragment } from 'react';
import Avatar from '../../components/Avatar';
import { useParams, useHistory } from 'react-router-dom';
import { useDocument } from '../../hooks/useDocument';
import { useAuthContext } from '../../hooks/useAuthContext';
import firebase from 'firebase/app';
import 'firebase/firestore';
import Navbar from '../../components/Navbar';
import EventAttendees from '../../components/EventAttendees';
import EventRegister from '../../components/EventRegister';
import EventOrganizer from '../../components/EventOrganizer';
import formatDistanceToNow from 'date-fns/formatDistanceToNow';
import Linkify from 'react-linkify';
import Alert from '@mui/material/Alert';
import { HashLink } from 'react-router-hash-link';


// --- Hardcoded speaker sessions for a single event (non-invasive) ---
const TARGET_EVENT_ID = '2pGiX9fFqNPr2pnQPpRq'; 


// --- Hardcoded speaker sessions for a single event (non-invasive) ---
const TARGET_EVENT_ID_2 = 'MDu6EtmnffTSV53oaFd7';

// --- Hardcoded program for a single event (non-invasive) ---
const HARDCODED_PROGRAM_2 = {
  [TARGET_EVENT_ID_2]: [
    {
      title: 'Talks',
      duration: '120min',
      items: [
        {
          label: 'Beyond Neuromodulation: Harnessing Temporal Interference Stimulation for Neural Regeneration',
          speaker: 'Sofia Peressotti',
          affiliation: 'Imperial College London, UK & University of Geneva, CH',
        },
        {
          label: 'Frequency-Specific Effects of TIS on Motor Cortical Excitability',
          speaker: 'Thi Xuan Dieu Nguyen',
          affiliation: 'Chang Gung University, TW',
        },
        {
          label: 'Validation of Temporal Interference Stimulation in Humans: Effects on Local Field Potentials and Single Neuron Activity',
          speaker: 'Raul Castillo Astorga',
          affiliation: 'University of California, Davis, USA',
        },
        {
          label: 'Resonance-driven enhancement of sleep spindles using thalamic temporal interference stimulation',
          speaker: 'Tobias Raufeisen',
          affiliation: 'University of Surrey, UK',
        },
        {
          label: 'Non-invasive temporal interference relieves neuropathic pain through subthreshold envelope modulation of the human spinal cord',
          speaker: 'Yujia Luo',
          affiliation: 'Zhejiang University School of Medicine, The Central Hospital of Yongzhou & University of South China, CN',
        },
        {
          label: 'Temporal Interference Stimulation of the Basal Ganglia: From Motor Symptoms to Motivation in Parkinson’s Disease',
          speaker: 'Johannes Stalter',
          affiliation: 'University of Oldenburg, DE',
        },
      ],
    },
  ],
};

const HARDCODED_SPEAKER_SESSIONS_2 = {
  [TARGET_EVENT_ID_2]: [
    {
      title: 'From Cells to Rhythms: Mechanistic Insights into Temporal Interference Stimulation',
      people: [
        { name: 'Sofia Peressotti', affiliation: 'Imperial College London, UK & University of Geneva, Switzerland', topic: 'Neural Regeneration' },
        { name: 'Thi Xuan Dieu Nguyen', affiliation: 'Chang Gung University, Taiwan', topic: 'Motor Cortical Excitability' },
        { name: 'Raul Castillo Astorga', affiliation: 'University of California, Davis, USA', topic: 'Validation in Humans (LFP & Single Neuron)' },
        { name: 'Tobias Raufeisen', affiliation: 'University of Surrey, UK', topic: 'Sleep Spindles' },
        { name: 'Yujia Luo', affiliation: 'Zhejiang University School of Medicine, The Central Hospital of Yongzhou & University of South China, China', topic: 'Neuropathic Pain' },
        { name: 'Johannes Stalter', affiliation: 'University of Oldenburg, Germany', topic: 'Parkinson’s Disease'},
      ],
    },
  ],
};


// --- Hardcoded program for a single event (non-invasive) ---
const HARDCODED_PROGRAM = {
  [TARGET_EVENT_ID]: [
    {
      title: 'Welcome',
      duration: '5min',
      items: [
        {
          label: 'Adam Williamson',
          speaker: 'Adam Williamson',
          affiliation: 'FNUSA, CR',
        },
      ],
    },
    {
      title: 'Deployment principles',
      duration: '30min',
      items: [
        {
          label: 'Equipment (setting-up, testing, reporting)',
          speaker: 'Nir Grossman',
          affiliation: 'Imperial College London',
          duration: '10min',
        },
        {
          label: 'Modelling (electrode configuration planning)',
          speaker: 'Esra Neufeld',
          affiliation: 'IT’IS, CH',
          duration: '10min',
        },
        { label: 'Q&A and Discussion', duration: '10min' },
      ],
      questions: [
        'What are the considerations related to the TI carrier frequency?',
        'What are the considerations related to personalised models?',
        'What are the considerations related to dose (amplitude, duration)?',
      ],
    },
    {
      title: 'Healthy human studies, examples',
      duration: '40min',
      items: [
        {
          label: 'Memory',
          speaker: 'Ines Violante',
          affiliation: 'King’s College London, UK',
          duration: '10min',
        },
        {
          label: 'Sleep Physiology',
          speaker: 'Melanie Boly',
          affiliation: 'University of Wisconsin, USA',
          duration: '10min',
        },
        {
          label: 'Attention',
          speaker: 'Michael Treadway',
          affiliation: 'Emory University, USA',
          duration: '10min',
        },
        { label: 'Q&A and Discussion', duration: '10min' },
      ],
      questions: [
        'What was the experimental equipment?',
        "What was the participant's setup procedure?",
        'What were the experimental design considerations?',
      ],
    },
    {
      title: 'Clinical studies, examples',
      duration: '40min',
      items: [
        {
          label: 'Motor Memory & Traumatic Brain Injury',
          speaker: 'Friedhelm Hummel',
          affiliation: 'EPFL, CH',
          duration: '10min',
        },
        {
          label: 'Parkinson’s Disease',
          speaker: 'Chencheng Zhang & Yu Liu',
          affiliation: 'Jiaotong University, CN & Shanghai University of Sport, CN',
          duration: '10min',
        },
        {
          label: 'Epilepsy',
          speaker: 'Adam Williamson',
          affiliation: 'FNUSA, CR',
          duration: '10min',
        },
        { label: 'Q&A and Discussion', duration: '10min' },
      ],
      questions: [
        'What was the experimental equipment?',
        "What was the participant's setup procedure?",
        'What were the experimental design considerations?',
      ],
    },
    {
      title: 'Summary',
      duration: '5min',
      items: [
        {
          label: 'Adam Williamson',
          speaker: 'Adam Williamson',
          affiliation: 'FNUSA, CR',
        },
      ],
    },
  ],
};


const HARDCODED_SPEAKER_SESSIONS = {
   ...HARDCODED_SPEAKER_SESSIONS_2, 
  [TARGET_EVENT_ID]: [
    {
      title: 'Session 1. Deployment principles',
      people: [
        { name: 'Nir Grossman', affiliation: 'Imperial College London, UK', topic: 'Equipment' },
        { name: 'Esra Neufeld', affiliation: 'ITIS Foundation, Switzerland', topic: 'Modelling' },
      ],
    },
    {
      title: 'Session 2. Healthy human studies examples',
      people: [
        { name: 'Ines Violante', affiliation: 'King’s College London, UK', topic: 'Memory' },
        { name: 'Melanie Boly', affiliation: 'University of Wisconsin, USA', topic: 'Sleep' },
        { name: 'Michael Treadway', affiliation: 'Emory University, USA', topic: 'Attention' },
      ],
    },
    {
      title: 'Session 3. Clinical studies examples',
      people: [
        { name: 'Friedhelm Hummel', affiliation: 'EPFL, Switzerland', topic: 'Motor Memory & Traumatic Brain Injury' },
        { name: 'Chencheng Zhang', affiliation: 'Jiaotong University, China', topic: 'Parkinson’s Disease' },
        { name: 'Adam Williamson', affiliation: 'Linköping University, Sweden / St. Anne’s University Hospital, Czech Republic', topic: 'Epilepsy' },
      ],
    },
  ],
};



export default function Event() {
  const { id } = useParams();
  const { error, document } = useDocument('events', id);
  const { user } = useAuthContext();
  const history = useHistory();

  const [postText, setPostText] = useState('');
  const [userFirstName, setUserFirstName] = useState('');
  const [userLastName, setUserLastName] = useState('');
  const db = firebase.firestore();

  // load user profile info for discussion posts
  useEffect(() => {
    if (user) {
      db.collection('users').doc(user.uid).get()
        .then(docSnap => {
          if (docSnap.exists) {
            const u = docSnap.data();
            setUserFirstName(u.first_name);
            setUserLastName(u.last_name);
          }
        })
        .catch(console.error);
    }
  }, [user, db]);

  if (error) return <div className="error">{error}</div>;
  if (!document) return <div className="loading" />;

  // determine if event is upcoming
  const eventDate = document.date?.toDate();
  const isUpcoming = eventDate ? eventDate > new Date() : false;

  const handleSubmit = async e => {
    e.preventDefault();
    if (!user) return;

    const randomId = user.displayName || user.uid;
    const createRandomId = `${randomId.slice(0,4)}-${Math.random()}`;
    const discussionToAdd = {
      photoURL: user.photoURL,
      content: postText,
      createdAt: firebase.firestore.Timestamp.fromDate(new Date()),
      id: createRandomId,
      comment_by: user.uid,
      first_name: userFirstName,
      last_name: userLastName,
    };
    await db.collection('events').doc(id).update({
      discussion: [...document.discussion, discussionToAdd]
    });
    setPostText('');
  };

  // Compose one line like: "Equipment (setting-up...), Nir Grossman (Imperial College London), 10min"
const formatProgramLine = (it = {}) => {
  const parts = [];
  if (it.label) parts.push(it.label);
  if (it.speaker) parts.push(`${it.speaker}${it.affiliation ? ` (${it.affiliation})` : ''}`);
  if (it.duration) parts.push(it.duration);
  return parts.join(', ');
};


  return (
    <div key={document.id} className="eventContainer">
      <Navbar />

      {/* Header Image & Alerts */}
      <div className="eventHeader">
        <div className="event-image">
           {/* {document.zoom_url && <span className="live-badge">LIVE</span>} */}
                {/* { document.zoom_url && <span className="soon-badge">STARTING SOON</span>} */}
          <img src={document.img_url} alt="" />        
         {/* {user && document.zoom_url && <Alert severity="success">This webinar is starting soon. You can join the discussion and ask questions <a href="#qa">here</a>. If you haven't registered, you can still attend! <a target="a_blank" href={document.zoom_url}>Join the Webinar via Zoom</a></Alert>} */}
          {/* {document.starting_soon && <Alert severity="info">This webinar is taking place today (Thursday, May 15 at 11 AM (ET)). The discussion Board is now open. Feel free to start asking questions about the webinar <a href="#qa">here</a>. Please note that the Zoom link to join the Webinar will be posted on this page 15 minutes before the webinar start time.</Alert>} */}
          {/* {user && document.zoom_url && <Alert severity="success">This webinar is currently running. You can join the discussion and ask questions <a href="#qa">here</a>. If you haven't registered, you can still attend! <a target="a_blank" href={document.zoom_url}>Join the Webinar via Zoom</a></Alert>} */}

          {document.canceled && (
            <Alert severity="info">
              This webinar is canceled. We apologize for any inconvenience.
            </Alert>
          )}
          {document.day === '' && (
            <Alert severity="info" className="finalization-alert">
              Webinar details are being finalized—register below to stay updated!
            </Alert>
          )}
        </div>

        {/* Main Content */}
        <div className="event-wrapper">
          <div className="event-left">
            <h1>{document.name}</h1>
            <p className="event-d-t">
              {document.day === ''
                ? 'Date: TBD'
                : `${document.day}, ${document.month} ${document.day_digit}, ${document.year} | ${document.start_time} - ${document.end_time} (ET)`}
            </p>
            <p>{document.summary}</p>

            {user && document.zoom_url &&  (
              <div className="qa-cta-top">
                <button className="btn-qa">
                  <HashLink smooth to="#qa">
                    Join Discussion / Q&A
                  </HashLink>
                </button>
              </div>
            )}
         {/* Webinar Recording */}
{!isUpcoming && (
  <>
    <h3 id="web-recording">Webinar Recording</h3>
    {document.talk_url ? (
      !user ? (
        <Alert
          severity="info"
          action={
            <button
              onClick={() => history.push('/login')}
              className="verify-btn"
            >
              Sign in
            </button>
          }
        >
          Please sign in to watch the recording.
        </Alert>
      ) : (
        <div className="video-player">
          <iframe
            src={document.talk_url}
            frameBorder="0"
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            title="Talk Video"
          />
        </div>
      )
    ) : (
      <Alert severity="warning">
     This webinar recording will be available soon—please check back later.
      </Alert>
    )}
  </>
)}

            <EventOrganizer event={document} />

            {/* Overview & Speakers */}
            <div className="event-overview">
              <h3>Overview</h3>
              <p>{document.overview}</p>
        

<h3>{HARDCODED_SPEAKER_SESSIONS[id] ? 'Program' : 'Speakers'}</h3>

{HARDCODED_SPEAKER_SESSIONS[id] ? (
  // Show PROGRAM just for the target event
  <div className="speakers-by-session">
    {HARDCODED_SPEAKER_SESSIONS[id].map((session) => (
      <div key={session.title} className="speaker-session">
        <h4>{session.title}</h4>
        <ul>
          {session.people.map((p) => (
            <li key={`${session.title}-${p.name}`}>
              - <strong>{p.name}</strong> ({p.affiliation}){p.topic ? `, ${p.topic}` : ''}
            </li>
          ))}
        </ul>
      </div>
    ))}
  </div>
) : (
  // Fall back to normal SPEAKERS on all other events
  <ul>
    {(document.speakers || []).map((s) => (
      <li key={s.id}>- {s.first_name} {s.last_name}</li>
    ))}
  </ul>
)}


            </div>


            {/* Discussion */}
            {user && document.zoom_url && (
              <div className="qanda">
                <h3>Discussion</h3>
                <Alert severity="warning">
                  Please abide by the Code of Conduct.{' '}
                  <a
                    target="_blank"
                    rel="noreferrer"
                    href="https://neuromodec.org/webinar-series-2024/code-of-conduct.html"
                  >
                    Read more.
                  </a>
                </Alert>
               { id === TARGET_EVENT_ID && (
  <Alert severity="info">
    Attendees are encouraged to submit questions below before the start of the webinar.
  </Alert>
)}
                <form onSubmit={handleSubmit}>
                  <div className="post-wrapper">
                    <div className="post-input-box">
                      <div className="post-img-wrap">
                        <Avatar src={user.photoURL} className="avatar" />
                      </div>
                      <div className="post-text">
                        <textarea
                          required
                          placeholder="Ask a question or reply to one..."
                          onChange={e => setPostText(e.target.value)}
                          value={postText}
                        />
                      </div>
                      <div id="qa" className="post-cta">
                        <button className="btn">Post</button>
                      </div>
                    </div>
                  </div>
                </form>
                <ul>
                  {document.discussion.map(disc => (
                    <li key={disc.id}>
                      <div className="user-post-wrap">
                        <div className="user-post-header">
                          <img
                            src={disc.photoURL}
                            alt="User"
                            className="avatar"
                          />
                          <p>
                            {disc.first_name} {disc.last_name}{' '}
                            <span>
                              {formatDistanceToNow(
                                disc.createdAt.toDate(),
                                { addSuffix: true }
                              )}
                            </span>
                          </p>
                        </div>
                        <div className="user-post-details">
                          <p>
                            <Linkify>{disc.content}</Linkify>
                          </p>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Attendees List */}
            <div className="event-attendees">
              <EventAttendees event={document} />
            </div>
          </div>

          {/* Right Sidebar */}
          <div className="event-right">
            <EventRegister event={document} />
          </div>
        </div>
      </div>
    </div>
  );
}
